import { useRef, useState, useEffect } from 'react'
import {
  Modal,
  ModalProps,
  Text,
  Stack,
  Button,
  ModalFooter,
  formatCurrency,
  IconButton,
  Box,
  TextInput,
  DayMonthYearInput,
  FormErrorMessage,
  FormHelperText,
  Checkbox,
} from '@thirstycamel/ui'
import omit from 'lodash.omit'
import type { CardDetails } from '@ts/core/src/modules/payment/payment.interfaces'

import { FRAUD_CHECK_ORDER_AMOUNT } from '../../utils/constants'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { useUser } from '../../hooks/useUser'

type ErrorFields = 'lastFour' | 'dob'

dayjs.extend(customParseFormat)

const ModalOrderFraudCheckConfirmButton = ({
  label,
  value,
  onToggle,
  isConfirmed,
  ...restProps
}) => {
  return (
    <Stack isInline spacing={5} justify="space-between" {...restProps}>
      <Box>
        <Text fontSize="sm">{label}:</Text>
        <Text fontWeight="bold" fontSize="lg">
          {value}
        </Text>
        <FormHelperText mt={1}>This name should match their ID</FormHelperText>
      </Box>

      <IconButton
        icon="check"
        isRound
        variant={isConfirmed ? 'solid' : 'outline'}
        variantColor="green"
        size="lg"
        onClick={onToggle}
      />
    </Stack>
  )
}

export interface ModalOrderFraudCheckProps extends ModalProps {
  onDismiss?: () => void
  onConfirm?: () => void
  cardDetails?: CardDetails
  dateOfBirth?: string
  customerName?: string
  billingAddress?: string
}

const ModalOrderFraudCheck: React.FC<ModalOrderFraudCheckProps> = ({
  isVisible,
  onDismiss,
  onConfirm,
  cardDetails,
  dateOfBirth,
  customerName,
  billingAddress,
}) => {
  const { isAdmin } = useUser()

  const initialFocusRef = useRef(null)
  const dobInputRef = useRef(null)

  const [dob, setDob] = useState('')
  const [errors, setErrors] = useState<{ [key in ErrorFields]?: string }>({})
  const [verifiedLastFour, setVerifiedLastFour] = useState(false)
  const [verifiedAddress, setVerifiedAddress] = useState(false)
  const [verifiedCustomerName, setVerifiedCustomerName] = useState(false)

  // Wallet payments (Apple/Google Pay) expose a device/virtual card whose last 4 digits
  // won't match the physical card the customer carries, so we skip the card-detail check for them.
  const isWalletPayment = Boolean(cardDetails?.wallet)

  const cardLastFour = cardDetails?.number?.slice(-4) || '-'
  const isReadyToSubmit =
    dob && verifiedAddress && verifiedCustomerName && (isWalletPayment || verifiedLastFour)

  const amount = formatCurrency(FRAUD_CHECK_ORDER_AMOUNT / 100, { truncateZeros: true })

  const handleChangeDob = value => {
    setDob(value)
    if (errors.dob) setErrors(state => omit(state, 'dob'))
  }

  const handleConfirm = () => {
    const enteredDob = dayjs(dob, 'DD-MM-YYYY', true)
    const isDobValid =
      enteredDob.isValid() && Math.abs(dayjs(dateOfBirth).diff(enteredDob, 'day')) <= 1 // 1 day lenience for simplicity with timezones etc

    const _errors: typeof errors = {}

    if (!isDobValid) _errors.dob = 'Incorrect date of birth.'

    setErrors(_errors)

    if (Object.keys(_errors).length > 0) return

    onDismiss?.()
    onConfirm?.()
  }

  return (
    <Modal
      onClose={onDismiss}
      isVisible={isVisible}
      initialFocusRef={initialFocusRef}
      heading="Fraud Check"
    >
      <Stack spacing={5}>
        <Stack spacing={2} pr={4}>
          <Text fontWeight="bold" fontSize="lg" w="15rem">
            Fraud checks are required for orders
          </Text>
          <Text>
            Before handing the customer their order, please confirm the customer's{' '}
            <b>photo ID{isWalletPayment ? '' : ', payment card'} and billing address</b> match the
            order details.
          </Text>
          <Text>
            The customer acknowledged during checkout that{' '}
            <b>
              they must bring their ID
              {isWalletPayment ? '' : ' and card used for payment'}
            </b>{' '}
            when collecting this order.
          </Text>
        </Stack>

        <Stack bg="gray.50" border="1px" borderColor="gray.200" p={5} spacing={4}>
          <Box>
            <Text fontSize="sm">Customer name:</Text>
            <Text fontWeight="bold" fontSize="lg">
              {customerName || cardDetails?.full_name || '-'}
            </Text>
            <FormHelperText mt={1}>This name should match their photo ID</FormHelperText>
          </Box>

          {isWalletPayment ? (
            <Box>
              <Text fontSize="sm">Payment method:</Text>
              <Text fontWeight="bold" fontSize="lg">
                {cardDetails?.wallet}
              </Text>
              <FormHelperText mt={1}>
                Paid with {cardDetails?.wallet} — no card digits to verify for this order.
              </FormHelperText>
            </Box>
          ) : (
            <Box>
              <Text fontSize="sm">Payment card last 4 digits:</Text>
              <Text fontWeight="bold" fontSize="lg">
                {cardLastFour}
              </Text>
              <FormHelperText mt={1}>
                These digits should match the card they present
              </FormHelperText>
            </Box>
          )}

          <Box>
            <Text fontSize="sm">Billing address:</Text>
            <Text fontWeight="bold" fontSize="lg">
              {billingAddress || '-'}
            </Text>
            <FormHelperText mt={1}>This address should match the customer details</FormHelperText>
          </Box>

          <Stack spacing={1}>
            <Text fontSize="sm">Date of Birth:</Text>
            <Box>
              <DayMonthYearInput
                onChangeValue={handleChangeDob}
                isInvalid={!!errors.dob}
                ref={dobInputRef}
                dateFormat={'DD-MM-YYYY'}
                order="day-month-year"
              />
              {errors.dob && <FormErrorMessage error={errors.dob} />}
              <FormHelperText>The date of birth as shown on their ID</FormHelperText>
            </Box>
          </Stack>

          <Stack spacing={3}>
            <Checkbox
              isChecked={verifiedCustomerName}
              onChangeValue={setVerifiedCustomerName}
              alignItems="baseline"
            >
              I have verified the customer name against their photo ID
            </Checkbox>

            {!isWalletPayment && (
              <Checkbox
                isChecked={verifiedLastFour}
                onChangeValue={setVerifiedLastFour}
                alignItems="baseline"
              >
                I have verified the last 4 digits of the credit card used for payment
              </Checkbox>
            )}

            <Checkbox
              isChecked={verifiedAddress}
              onChangeValue={setVerifiedAddress}
              alignItems="baseline"
            >
              I have verified the billing address with the customer
            </Checkbox>
          </Stack>
        </Stack>

        {isAdmin && (
          <Button
            variantColor="blue"
            isRound
            onClick={() => {
              onDismiss?.()
              onConfirm?.()
            }}
          >
            Admin Approve
          </Button>
        )}

        <ModalFooter>
          <Button
            variant="outline"
            variantColor="pink"
            ref={initialFocusRef}
            isRound
            mr={3}
            onClick={onDismiss}
          >
            Cancel
          </Button>

          <Button variantColor="pink" isRound onClick={handleConfirm} isDisabled={!isReadyToSubmit}>
            Confirm
          </Button>
        </ModalFooter>
      </Stack>
    </Modal>
  )
}

export default ModalOrderFraudCheck
